import { cx } from '@emotion/css';
import {
  Accordion as MUIAccordion,
  AccordionDetails as MUIAccordionDetails,
  AccordionSummary as MUIAccordionSummary,
  Box,
  Stack,
  styled,
  Theme,
  Typography,
} from '@mui/material';
import { ComponentProps, ReactNode, useEffect, useMemo, useState } from 'react';
import { makeStyles } from 'tss-react/mui';

import { ChevronDownIcon } from '@/components/icons/ChevronDownIcon';
import {
  CONTAINER_BREAKPOINTS,
  containerQuery,
} from '@/styles/containerQueries';
import { COLORS } from '@/styles/tokens/colors';
import { FONT_WEIGHTS } from '@/styles/tokens/fonts';
import { WithClasses } from '@/styles/types';

import { useUnguardedAccordionContext } from './Accordion.context';

/**
 * Container wrapper that enables container queries for responsive layouts.
 * When `responsiveRightAdornment` is enabled, this wrapper establishes a
 * container context for the accordion content.
 */
const ResponsiveContainer = styled(MUIAccordion)(() => ({
  containerType: 'inline-size',
}));

/**
 * Responsive wrapper for accordion summary content that switches from
 * row to column layout below the responsive breakpoint.
 */
const ResponsiveSummaryContent = styled(Stack)(
  ({ theme }: { theme: Theme }) => ({
    width: '100%',
    [containerQuery(theme).down(CONTAINER_BREAKPOINTS.RESPONSIVE_STACK)]: {
      flexDirection: 'column',
      alignItems: 'flex-start',
      justifyContent: 'flex-start',
      gap: theme.spacing(1),
    },
  })
);

/**
 * Wrapper for the right adornment that takes full width when stacked.
 */
const ResponsiveRightAdornment = styled(Box)(({ theme }: { theme: Theme }) => ({
  [containerQuery(theme).down(CONTAINER_BREAKPOINTS.RESPONSIVE_STACK)]: {
    width: '100%',
  },
}));

export type AccordionVariant =
  | 'default'
  | 'dashed'
  | 'filled'
  | 'filled-teal'
  | 'inner-shadow';

const useAccordionSummaryStyles = makeStyles<{
  expansionDisabled: boolean;
}>({ name: { MUIAccordionSummary } })((_theme, { expansionDisabled }) => ({
  root: {
    padding: '0px',
    minHeight: '0px',
    '&.Mui-expanded': {
      padding: '0px',
      minHeight: '0px',
    },
    '& .MuiAccordionSummary-content': {
      margin: '0px',
      padding: '0px',
      paddingRight: '16px',
      // Allow this flex item to shrink below its content's intrinsic width so
      // that descendants using `noWrap` + ellipsis can actually truncate
      // instead of pushing the row past the card's right edge.
      minWidth: 0,
      '&.Mui-expanded': {
        margin: '0px',
        padding: '0px',
        paddingRight: '16px',
      },
    },
    ...(expansionDisabled && {
      cursor: 'default !important',
      '&:hover': {
        cursor: 'default !important',
      },
    }),
  },
}));

const useAccordionDetailsStyles = makeStyles<{
  expanded: boolean;
  showDivider: boolean;
}>({
  name: { MUIAccordionDetails },
})((theme, { expanded, showDivider }) => ({
  root: {
    marginTop: showDivider ? theme.spacing(2) : 0,
    borderTop: showDivider
      ? expanded
        ? `1px solid ${COLORS.GRAY[300]}`
        : `1px solid transparent`
      : '',
    padding: '0px',
    paddingTop: theme.spacing(2),
  },
}));

const useStyles = makeStyles<{
  expanded: boolean;
  disableExpandedMargin: boolean;
}>()((theme, { expanded, disableExpandedMargin }) => ({
  root: {
    paddingTop: `${theme.spacing(1.5)} !important`,
    paddingRight: `${theme.spacing(2)} !important`,
    paddingBottom: expanded
      ? `${theme.spacing(1.5)} !important`
      : `${theme.spacing(1.5)} !important`,
    paddingLeft: `${theme.spacing(2)} !important`,
    backgroundColor: COLORS.PRIMITIVES.WHITE,
    '&.MuiAccordion-rounded': {
      borderRadius: theme.spacing(0.5),
    },
    ...(disableExpandedMargin && {
      '&.MuiAccordion-root.Mui-expanded': {
        margin: '0px',
      },
    }),
    border: `1px solid ${COLORS.GRAY[300]}`,
    borderRadius: theme.spacing(0.5),
    borderColor: expanded ? COLORS.GRAY[300] : 'transparent',
    transition: 'border-color 0.5s ease',
    boxShadow: 'none',
  },
}));

const useDefaultStyles = makeStyles()((theme) => ({
  root: {
    border: `1px solid ${COLORS.GRAY[200]}`,
    '&.MuiAccordion-rounded': {
      borderRadius: theme.spacing(0.5),
    },
  },
}));

const useFilledStyles = makeStyles<{ expanded: boolean }>()((theme) => ({
  root: {
    backgroundColor: COLORS.GRAY[50],
    border: `1px solid ${COLORS.GRAY[200]}`,
    '&.MuiAccordion-rounded': {
      borderRadius: theme.spacing(0.5),
    },
  },
}));

const useFilledTealStyles = makeStyles<{ expanded: boolean }>()((theme) => ({
  root: {
    backgroundColor: COLORS.TEAL['50'],
    border: `1px solid ${COLORS.TEAL[200]}`,
    '&.MuiAccordion-rounded': {
      borderRadius: theme.spacing(0.5),
    },
  },
}));

const useDashedStyles = makeStyles<{ expanded: boolean }>()((theme) => ({
  root: {
    border: `1px dashed ${COLORS.GRAY[300]}`,
    '&.MuiAccordion-rounded': {
      borderRadius: theme.spacing(0.5),
    },
  },
}));

const useInnerShadowStyles = makeStyles<{ expanded: boolean }>()((theme) => ({
  root: {
    backgroundColor: COLORS.GRAY[100],
    boxShadow: theme.palette.shadows.mdInset,
    border: `1px solid ${COLORS.GRAY[200]}`,
    '&.MuiAccordion-rounded': {
      borderRadius: theme.spacing(0.5),
    },
  },
}));

type MUIAccordionProps = ComponentProps<typeof MUIAccordion>;

export interface AccordionProps
  extends Omit<MUIAccordionProps, 'variant' | 'title' | 'onChange'> {
  classes?: WithClasses<typeof useDefaultStyles>;
  variant: AccordionVariant;
  leftAdornment?: ReactNode;
  rightAdornment?: ReactNode;
  labelContainerProps?: ComponentProps<typeof Stack>;
  title: ReactNode;
  subtitle?: string | ReactNode;
  description?: ReactNode;
  isDefaultExpanded?: boolean;
  onChange?: (expanded: boolean) => void;
  hideSubtitleOnOpen?: boolean;
  id?: string;
  withDivider?: boolean;
  expandIconProps?: ComponentProps<typeof ChevronDownIcon>;
  /**
   * When true, prevents the accordion from expanding/collapsing and hides the expand icon.
   * This is useful for cases where you want a non-interactive accordion that looks clean
   * without the expansion controls when there's no expandable content.
   */
  expansionDisabled?: boolean;
  /**
   * When true, enables responsive behavior where the rightAdornment stacks below
   * the header content when the accordion container width is below 840px.
   * Uses CSS container queries for responsive layout.
   */
  responsiveRightAdornment?: boolean;
  /**
   * When true, keeps the root margin at 0 while expanded to prevent layout shift.
   */
  disableExpandedMargin?: boolean;
}

export function Accordion({
  classes: externalClasses,
  variant,
  leftAdornment,
  rightAdornment,
  title,
  subtitle,
  description,
  labelContainerProps = {},
  isDefaultExpanded = false,
  onChange,
  hideSubtitleOnOpen,
  id,
  withDivider = true,
  expandIconProps = {},
  expansionDisabled = false,
  responsiveRightAdornment = false,
  disableExpandedMargin = false,
  ...props
}: Omit<AccordionProps, 'disableGutters'>) {
  const [expanded, setExpanded] = useState(isDefaultExpanded);

  const possibleAccordionContext = useUnguardedAccordionContext();
  const closedAccordionIds = useMemo(() => {
    return possibleAccordionContext?.closedAccordionIds ?? [];
  }, [possibleAccordionContext?.closedAccordionIds]);

  useEffect(() => {
    // we don't want to collapse something that's already open just becuase the new "isDefaultExpanded" is false
    if (isDefaultExpanded !== expanded && isDefaultExpanded === true) {
      setExpanded(isDefaultExpanded);
    }
    // no exhaustive deps because we only want to rerun this if the default expanded prop changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDefaultExpanded]);

  const handleAccordionChange = () => {
    // Don't allow expansion if expansion is disabled
    if (expansionDisabled) {
      return;
    }
    setExpanded(!expanded);
    onChange?.(!expanded);
  };

  const { classes } = useStyles(
    { expanded, disableExpandedMargin },
    {
      props: {
        classes: externalClasses,
        ownerState: { expanded, disableExpandedMargin },
      },
    }
  );

  const { classes: defaultClasses } = useDefaultStyles();
  const { classes: dashedClasses } = useDashedStyles({ expanded });
  const { classes: filledClasses } = useFilledStyles({ expanded });
  const { classes: filledTealClasses } = useFilledTealStyles({ expanded });
  const { classes: innerShadowClasses } = useInnerShadowStyles({ expanded });
  const { classes: summaryClasses } = useAccordionSummaryStyles({
    expansionDisabled,
  });
  const showDivider = withDivider === true;
  const { classes: detailsClasses } = useAccordionDetailsStyles({
    expanded,
    showDivider,
  });

  const variantClasses: Record<AccordionVariant, typeof classes> = {
    default: defaultClasses,
    dashed: dashedClasses,
    filled: filledClasses,
    'filled-teal': filledTealClasses,
    'inner-shadow': innerShadowClasses,
  };

  const variantStyles = variantClasses[variant];

  const showSubtitle = useMemo(() => {
    if (!hideSubtitleOnOpen) {
      return true;
    }

    return !expanded;
  }, [expanded, hideSubtitleOnOpen]);

  useEffect(() => {
    if (!id || !closedAccordionIds.length || isDefaultExpanded) {
      return;
    }
    setExpanded(!closedAccordionIds.includes(id));
  }, [closedAccordionIds, id, isDefaultExpanded]);

  // Choose between responsive or standard accordion wrapper
  const AccordionWrapper = responsiveRightAdornment
    ? ResponsiveContainer
    : MUIAccordion;

  // Choose between responsive or standard content wrappers
  const SummaryContentWrapper = responsiveRightAdornment
    ? ResponsiveSummaryContent
    : Stack;
  const RightAdornmentWrapper = responsiveRightAdornment
    ? ResponsiveRightAdornment
    : Box;

  return (
    <AccordionWrapper
      {...props}
      disableGutters
      expanded={expanded}
      onChange={handleAccordionChange}
      className={cx(classes.root, variantStyles.root)}
    >
      <MUIAccordionSummary
        className={cx(summaryClasses.root)}
        expandIcon={
          expansionDisabled ? (
            <ChevronDownIcon
              size={16}
              {...expandIconProps}
              style={{ ...expandIconProps.style, display: 'none' }}
            />
          ) : (
            <ChevronDownIcon size={16} {...expandIconProps} />
          )
        }
      >
        <SummaryContentWrapper
          direction="row"
          gap={1}
          width="100%"
          alignItems="center"
          justifyContent="space-between"
        >
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            justifyContent="start"
            // Take all leftover horizontal space and allow shrinking. Without
            // `flex-grow: 1`, the right adornment (with `noWrap` text + a huge
            // intrinsic basis) hogs the row under flex-shrink and forces the
            // title to wrap word-by-word at narrow widths.
            sx={{ flex: '1 1 auto', minWidth: 0 }}
          >
            {leftAdornment}
            <Stack spacing={0.25} {...labelContainerProps}>
              <Typography
                variant="title-s"
                sx={{
                  fontWeight: FONT_WEIGHTS.bold,
                }}
              >
                {title}
              </Typography>
              {subtitle &&
                showSubtitle &&
                (typeof subtitle === 'string' ? (
                  <Typography variant="body-xs">{subtitle}</Typography>
                ) : (
                  subtitle
                ))}
              {description && (
                <Typography variant="body-xs">{description}</Typography>
              )}
            </Stack>
          </Stack>
          {rightAdornment && (
            // Cap the right adornment so a long `noWrap` value can't push the
            // title side below ~half the row. The right side will truncate
            // (via the inner Typography's `noWrap` + ellipsis) before
            // crowding the title.
            <RightAdornmentWrapper sx={{ minWidth: 0, maxWidth: '50%' }}>
              {rightAdornment}
            </RightAdornmentWrapper>
          )}
        </SummaryContentWrapper>
      </MUIAccordionSummary>
      <MUIAccordionDetails
        className={cx(detailsClasses.root)}
        aria-hidden={!expanded}
      >
        <>{props.children}</>
      </MUIAccordionDetails>
    </AccordionWrapper>
  );
}
